import { useEffect, useRef, useState } from 'react'

import { useAuth } from '../../lib/auth/AuthContext.jsx'
import { resolveCollabUrl, roomName, colorForUser, editorsByAsset } from '../../lib/collabPresence.js'
import { registerCollabClient, unregisterCollabClient } from '../../lib/collabMappingBridge.js'

// usePresence connects to the collaboration service for one character and
// reports who else is on it. No-ops (empty roster) when there is no
// authenticated user or no character — presence requires identity.
//
// The Hocuspocus/Yjs client is DYNAMICALLY imported so those libraries stay out
// of the app's boot chunk — they'd otherwise load on every page and slow avatar
// startup (presence isn't on the render/animation path). They load lazily on
// first use here (the content browser), when a signed-in user opens a character.
export function usePresence(slug) {
  const { me } = useAuth()
  const clientRef = useRef(null)
  const [roster, setRoster] = useState([])

  const orgId = me?.org_id
  const userId = me?.id

  useEffect(() => {
    if (!userId || !orgId || !slug) return undefined

    let cancelled = false
    let client = null
    let off = null

    import('../../lib/CollabPresenceClient.js').then(({ CollabPresenceClient }) => {
      if (cancelled) return
      client = new CollabPresenceClient()
      clientRef.current = client
      client.connect({
        url: resolveCollabUrl(import.meta.env),
        room: roomName(orgId, slug),
        user: { id: userId, name: me?.email || userId, color: colorForUser(userId) },
      })
      off = client.onChange(setRoster)

      // Stage 2: expose the client to the mapping bridge so content-browser
      // edits route through the shared doc + remote edits flow back in.
      registerCollabClient(client)

      // Live handle for tests / debugging (CLAUDE.md window.__* convention).
      if (typeof window !== 'undefined') window.__COLLAB_PRESENCE__ = client
    })

    return () => {
      cancelled = true
      off?.()
      unregisterCollabClient(client)
      client?.destroy()
      clientRef.current = null
      setRoster([])
      if (typeof window !== 'undefined' && client && window.__COLLAB_PRESENCE__ === client) {
        window.__COLLAB_PRESENCE__ = null
      }
    }
  }, [userId, orgId, slug, me?.email])

  return {
    roster,
    editors: editorsByAsset(roster),
    setFocus: (assetId) => clientRef.current?.setFocus(assetId),
  }
}
